import React, { useEffect, useState } from 'react';
import "@/App.css";
import { BrowserRouter, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, LayoutGroup, motion } from 'framer-motion';
import { Toaster } from "sonner";
import axios from "axios";
import { AppRoutes } from './AppRoutes';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { CallProvider } from './contexts/CallContext';
import { RealtimeProvider } from './contexts/RealtimeProvider';
import { LogoLayoutProvider } from './contexts/LogoLayoutContext';
import { FloatingLogo } from './components/FloatingLogo';
import { EdgeSwipeHint } from './components/EdgeSwipeHint';
import AdminReturnPill from './components/AdminReturnPill';
import BlankScreenRecovery from './components/BlankScreenRecovery';
import CookieConsentBanner from './components/CookieConsentBanner';
import InAppBrowserBanner from './components/InAppBrowserBanner';
import PasskeyEnrollmentPrompt from './components/PasskeyEnrollmentPrompt';
import EnableNotificationsPrompt from './components/EnableNotificationsPrompt';
import AgreementReAcceptGate from './components/legal/AgreementReAcceptGate';
import { useSwipeBack } from './hooks/useSwipeBack';
import { isAppLaunch, isMarketingVisitor, clearMarketingVisitor } from './utils/launchContext';
import { rememberActiveRoleFromPath } from './utils/lastActiveRole';
import { ImpersonationBanner } from './components/admin/ImpersonationBanner';
import AnnouncementBanner from './components/AnnouncementBanner';
import IOSInstallBanner from './components/IOSInstallBanner';
import { identifyAirbridgeUser, forgetAirbridgeUser } from './utils/airbridge';
import { recordDevicePlatform } from './utils/devicePlatform';
import { hasLocalPasskeyFlag } from './utils/passkeys';
import { isFreshLaunch, markSessionAlive } from './utils/sessionGuard';
import { clearAuthSession, persistAuthToken } from './utils/authStorage';
import { registerFCMPush, setFCMPushNavigator, mirrorNativeAuthToken, clearNativeAuthToken } from './utils/fcmPush';
import { initNativeDeepLinks, waitForLaunchDeepLink } from './utils/nativeDeepLinks';
import { setPendingPartnerIntent, clearPendingPartnerIntent, clearRoleChosen } from './utils/partnerIntent';
import { clearRoleSwitchContext } from './utils/roleSwitchFlow';
import { isNativeOrInstalledApp } from './utils/runtimeEnv';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSplash, setShowSplash] = useState(false);
  const [initialPath] = useState(() => location.pathname);

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/service-worker.js').catch(() => {});
      // Listen for the "Copy invite" action postMessage sent by the
      // service worker when a host taps Copy on a browser/PWA
      // reservation-detected notification. The SW can't touch the
      // clipboard from its background context, so it hands the
      // pre-composed text off to us and we write it here.
      const handleSwMessage = (event) => {
        const payload = event?.data;
        if (!payload || payload.type !== 'allure-copy-invite' || !payload.text) return;
        try {
          if (navigator.clipboard?.writeText && window.isSecureContext) {
            navigator.clipboard.writeText(payload.text).catch(() => {});
          }
        } catch { /* ignore */ }
      };
      navigator.serviceWorker.addEventListener('message', handleSwMessage);
    }

    // Check for existing session
    const token = localStorage.getItem("token");
    const storedUser = localStorage.getItem("user");

    // Bank-app pattern: on EVERY cold launch where the device has a
    // registered passkey, force biometric unlock — even if the JWT is
    // still valid. We never want a stolen unlocked phone to walk past
    // the Allure lock screen. Background→resume of the SAME process
    // does NOT trigger this (sessionStorage survives backgrounding),
    // only a true cold launch does.
    const coldLaunch = isFreshLaunch();
    const passkeyOnDevice = hasLocalPasskeyFlag();
    // Only enforce biometric unlock inside the actual native app (Capacitor
    // WebView) or an installed PWA. A plain browser tab — e.g. tapping
    // "Learn more" from a venue outreach email on allurexp.com/for-venues —
    // must NEVER pop the biometric prompt. See utils/runtimeEnv.js.
    const nativeOrInstalled = isNativeOrInstalledApp();
    const PUBLIC_PATHS = new Set([
      '/', '/welcome', '/about', '/contact', '/privacy', '/terms',
      '/login', '/register', '/forgot-password', '/reset-password',
      '/verify-email', '/two-factor', '/unlock',
      '/google/auth-callback', '/google/complete',
      // Google OAuth webview handoff — Capacitor navigates the WebView
      // here during the login round-trip. If it were NOT in this set,
      // a cold-launch that lands on this path with a passkey on device
      // would bounce to /unlock and pop the OS credential picker with
      // stale emails — exactly the confusing behaviour reported.
      '/app-google-login', '/auth/google/callback', '/app-login-complete',
      '/account-deleted', '/delete-account',
      // Marketing / venue outreach landing pages — reached from emails on
      // plain browsers; must never trigger the biometric gate.
      '/for-venues', '/venue-partners', '/legal/imprint', '/imprint',
      // Viva Wallet redirects here inside a Chrome Custom Tab after
      // checkout. The Custom Tab shares localStorage with the main
      // WebView, so hasLocalPasskeyFlag() is true — but we must NOT
      // bounce to /unlock or we'll pop the passkey picker with random
      // emails inside the Custom Tab overlay.
      '/payment-complete',
      '/guest/payment-method/callback',
    ]);
    let cancelled = false;
    (async () => {
    // Native cold start: Capacitor loads the WebView at the remote ROOT url,
    // so initialPath is '/' even when the app was opened via an App Link
    // (/invite/XXXX). Wait briefly for the real launch deep link (launch
    // intent or install referrer, buffered since index.js) before deciding
    // on the /unlock redirect — otherwise the unlock/role-home navigation
    // stomps the invite. Resolves instantly on plain web.
    const launchDeepLink = await waitForLaunchDeepLink(2500);
    if (cancelled) return;
    const effectivePath = launchDeepLink || initialPath;
    // Deep-link paths that should bypass the lock screen so users don't
    // miss invites — they'll be re-prompted post-accept anyway.
    const isInviteDeepLink = /^\/(invite|host-invite|venue-invite|staff-invite)\b/.test(effectivePath);

    // Viva Wallet checkout flow: when a guest adds a card, the native
    // app opens a Chrome Custom Tab via @capacitor/browser. After
    // payment, Viva redirects the Custom Tab back to our domain. The
    // Custom Tab shares localStorage (same origin) so it sees the
    // passkey flag — but we must NEVER show the biometric unlock inside
    // the Custom Tab. Two independent signals:
    //  1. PUBLIC_PATHS already includes /payment-complete.
    //  2. The native-checkout flag is set before Browser.open() fires.
    let isPaymentCheckoutReturn = false;
    try {
      isPaymentCheckoutReturn =
        localStorage.getItem('allure_native_checkout_pending') === '1' ||
        initialPath.startsWith('/payment') ||
        initialPath.startsWith('/guest/payment-method');
    } catch (_e) { /* noop */ }

    if (token && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        // Restore Airbridge user identity on every reload so events stay
        // attributed to the right person across PWA sessions.
        identifyAirbridgeUser(parsedUser);
        // Record the device platform (ios/android/desktop) for this user.
        // Idempotent on the backend so re-firing across sessions is safe.
        recordDevicePlatform(token);
        // Native Android FCM registration — triggers the Android 13+ OS
        // permission dialog and registers the FCM token with the backend.
        // Idempotent (checks platform, existing permission, listener guard),
        // so safe to call on every cold launch. Critical for users who
        // cold-launch onto a deep link (e.g. after deferred install from
        // Google Play) with an already-restored session — handleLogin() is
        // never called in that flow, so without this the OS notification
        // permission prompt would never fire and pushes stay blocked.
        try {
          registerFCMPush(token);
        } catch { /* noop */ }
        // Also mirror the restored JWT into Capacitor Preferences on
        // cold-launch so lock-screen Decline still works after an app
        // restart (JS `handleLogin` isn't called on cold-launch).
        try {
          mirrorNativeAuthToken(token);
        } catch { /* noop */ }
        // Silent token renewal on cold launch. If the JWT is still valid
        // but is within its final week, /auth/renew mints a fresh 30-day
        // token so the user's session keeps rolling forever without
        // another sign-in. Errors are swallowed — if renew 401s, the
        // regular axios interceptor will surface the expired-session
        // toast + redirect to /login on the next real request.
        if (coldLaunch) {
          axios.post(`${API}/auth/renew`, {}, {
            headers: { Authorization: `Bearer ${token}` },
          }).then((res) => {
            const newToken = res?.data?.access_token;
            const newUser = res?.data?.user;
            if (newToken) persistAuthToken(newToken);
            if (newUser) {
              try { window.localStorage.setItem('user', JSON.stringify(newUser)); } catch { /* noop */ }
              setUser(newUser);
            }
          }).catch(() => { /* handled globally by axios interceptor */ });
        }
        if (nativeOrInstalled && coldLaunch && passkeyOnDevice && !PUBLIC_PATHS.has(effectivePath) && !isInviteDeepLink && !isPaymentCheckoutReturn) {
          // Hand off to /unlock — UnlockPage auto-fires the OS biometric
          // prompt on mount and calls completeLogin() on success.
          navigate('/unlock', { replace: true });
        } else {
          // Either there's no passkey to unlock with, or this isn't a
          // cold launch — treat the existing session as live.
          markSessionAlive();
          // Cold-launch + restored session + no passkey yet → also flag
          // the post-login passkey prompt. The prompt component re-checks
          // device support, prior enrollment and 30-day cooldown before
          // actually rendering, so this is safe to fire on every cold
          // launch without nagging.
          if (coldLaunch && !passkeyOnDevice) {
            try {
              window.sessionStorage.setItem('allure_passkey_prompt_pending', '1');
            } catch { /* noop */ }
          }
          // Cold-launch + restored session → also flag the lock-screen
          // notifications prompt. The prompt component re-checks browser
          // support, current permission state, existing subscription and a
          // 30-day cooldown before actually rendering, so this is safe to
          // fire on every cold launch without nagging. It also silently
          // re-subscribes users whose previous PushSubscription expired.
          if (coldLaunch) {
            try {
              window.sessionStorage.setItem('allure_notifications_prompt_pending', '1');
            } catch { /* noop */ }
          }
        }
      } catch {
        clearAuthSession();
        try { clearNativeAuthToken(); } catch { /* noop */ }
      }
    } else if (nativeOrInstalled && coldLaunch && passkeyOnDevice && !PUBLIC_PATHS.has(effectivePath) && !isInviteDeepLink && !isPaymentCheckoutReturn) {
      // No active session BUT this device has a registered passkey →
      // skip the password screen and unlock straight to the app.
      // Plain browser tabs (marketing links from emails) are excluded
      // by the nativeOrInstalled guard.
      navigate('/unlock', { replace: true });
    }

    setLoading(false);
    })();

    // NOTE: Auto-seeding via /api/seed was REMOVED on 2026-02 because it was
    // wiping live transaction records (e.g. real Viva payouts) every time a
    // fresh browser/incognito session loaded the app. Demo data should be
    // seeded explicitly via curl `POST /api/seed` when needed, never as a
    // side effect of app boot.
    return () => { cancelled = true; };
  }, [initialPath, navigate]);

  // Native Android deep links: on cold start the Capacitor WebView loads the
  // remote server ROOT url, dropping the App-Link path (/invite/XXXX etc.).
  // This reads the real launch URL from the @capacitor/app plugin and
  // navigates to it, and keeps listening for links while the app is open.
  // Runs AFTER the cold-start session effect above so an invite deep link
  // wins over the /unlock redirect (invites intentionally bypass the lock).
  useEffect(() => {
    initNativeDeepLinks(navigate);
    // Give the FCM push-tap listener a real React Router navigate() so
    // notification taps route reliably (replaces the fragile pushState +
    // PopStateEvent trick). Also flushes any tap that was buffered while
    // React Router was still cold-mounting.
    setFCMPushNavigator(navigate);
  }, [navigate]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    // Persist the last role-specific shell the user is inside so that the
    // next sign-in returns them to the same role (guest / host / venue
    // owner / venue staff / admin staff) instead of always defaulting to
    // whatever `user.role` is on their account.
    rememberActiveRoleFromPath(location.pathname);

    // Session-scoped "did the user navigate INSIDE the app yet?" sentinel.
    // Cold-start / fresh-app-launch reads this as 0 on the first mount and
    // it stays 0 for the initial route. Every subsequent React Router
    // navigation increments it. Pages with a "back" button consult this
    // so they know whether `navigate(-1)` is safe (in-app history exists)
    // or would land on about:blank / previous WebView entry (cold-start).
    try {
      const cur = Number(sessionStorage.getItem('allureAppNavCount') || '0');
      sessionStorage.setItem('allureAppNavCount', String(cur + 1));
    } catch { /* localStorage/sessionStorage unavailable */ }

    // Play the brand intro (logo fade + "Designed To Experience") once per
    // browser session whenever the user ENTERS the auth flow — either by
    // launching the installed app, or by tapping "Log in" / "Get started" on
    // the marketing website (which route to /login and /register). It never
    // plays over the public marketing pages themselves.
    const AUTH_SPLASH_PATHS = new Set(['/login', '/register']);
    if (!sessionStorage.getItem('hasSeenSplash') && (isAppLaunch() || AUTH_SPLASH_PATHS.has(location.pathname))) {
      setShowSplash(true);
    }
  }, [location.pathname]);

  const handleSplashComplete = () => {
    setShowSplash(false);
    sessionStorage.setItem("hasSeenSplash", "true");
  };

  const handleRoleSelect = (role) => {
    // Three-card flow (Feb 2026): each choice routes to its own onboarding
    // path with a durable localStorage intent flag so a Capacitor
    // cold-start mid-flow can never land the user in the wrong app.
    // Deep-link invitees never touch this page (SmartRootRouter buffers
    // the deep link and skips /select-role entirely).
    if (role === 'guest') {
      setPendingPartnerIntent('guest');
      navigate("/guest-info", { replace: true });
    } else if (role === 'host') {
      setPendingPartnerIntent('host');
      navigate("/host-intro-1", { replace: true });
    } else if (role === 'venue') {
      setPendingPartnerIntent('venue');
      navigate("/venue/intro", { replace: true });
    } else if (role === 'signin') {
      // "Already have an account? Sign in" footer link — no partner
      // intent yet, they'll land wherever their existing role lives.
      clearPendingPartnerIntent();
      navigate("/login", { replace: true });
    } else {
      navigate("/login", { replace: true });
    }
  };

  const handleLogin = (userData) => {
    setUser(userData);
    identifyAirbridgeUser(userData);
    recordDevicePlatform(localStorage.getItem('token'));
    // Tell ThemeContext (and any other subscribers) that a token is now
    // available so they can hydrate from the server. `storage` events only
    // fire across tabs, so we dispatch our own in-tab signal too.
    try { window.dispatchEvent(new CustomEvent('allure:auth-changed')); } catch { /* noop */ }
    // Successful login (any path: password, Google, 2FA, biometric)
    // counts as the session being unlocked. Subsequent renders skip
    // the unlock screen until the next cold start.
    markSessionAlive();
    // Flag for the post-login passkey enrollment prompt. The prompt component
    // decides whether to actually show based on device support, prior
    // enrollment, and 30-day cooldown. If the user just unlocked via passkey
    // (already enrolled) we skip the flag entirely.
    try {
      if (!hasLocalPasskeyFlag()) {
        window.sessionStorage.setItem('allure_passkey_prompt_pending', '1');
      }
    } catch { /* noop */ }
    // Flag for the post-login lock-screen notifications prompt. Mirrors the
    // passkey pattern: prompt component re-checks support / permission /
    // existing subscription / 30-day cooldown before rendering. Critical for
    // direct-message push to actually deliver — without this prompt, users
    // never call PushManager.subscribe and so /api/push/subscribe never
    // stores a row for their userId, which means the message fan-out has
    // nothing to fan to.
    try {
      window.sessionStorage.setItem('allure_notifications_prompt_pending', '1');
    } catch { /* noop */ }
    // Native Android FCM registration — triggers the Android 13+ OS permission
    // dialog and registers the FCM token with the backend. No-op on web/iOS.
    try {
      registerFCMPush(localStorage.getItem('token'));
    } catch { /* noop */ }
    // Mirror the fresh JWT into Capacitor Preferences so the native
    // DeclineCallActivity (on the lock-screen CallStyle notification)
    // can POST /calls/{id}/decline without opening the app. No-op on web.
    try {
      mirrorNativeAuthToken(localStorage.getItem('token'));
    } catch { /* noop */ }
  };

  const handleLogout = () => {
    setUser(null);
    forgetAirbridgeUser();
    // Wipe both localStorage and the native Preferences mirror so a
    // subsequent cold-launch on this device doesn't hydrate an
    // already-invalidated session.
    clearAuthSession();
    // Wipe the JWT mirror that the native DeclineCallActivity reads —
    // without this, a stale token would linger in Android
    // SharedPreferences and could POST /calls/{id}/decline as the
    // previous user for any future ringing card on this device.
    try { clearNativeAuthToken(); } catch { /* noop */ }
    // Clear the session-alive marker so next cold launch behaves as expected.
    try { sessionStorage.removeItem('allure_session_alive_v1'); } catch { /* noop */ }
    // Wipe the "role explicitly chosen" marker so a fresh account
    // registered on this device gets the /select-role picker again.
    clearRoleChosen();
    clearPendingPartnerIntent();
    // Wipe any leftover role-switch context so a fresh signup started in
    // the same browser tab (after logout) can never inherit a stale
    // "came from /switch-role" flag that would mis-route their back
    // arrow to the multi-role hub.
    clearRoleSwitchContext();
    // Marketing-site visitors go back to /welcome; installed-app users go to /login
    const cameFromMarketing = isMarketingVisitor();
    const isApp = isAppLaunch();
    clearMarketingVisitor();
    if (cameFromMarketing && !isApp) {
      navigate("/welcome", { replace: true });
    } else {
      navigate("/login", { replace: true });
    }
  };

  // Edge-swipe-back gesture (iOS-style). Listens globally and navigates(-1)
  // on commit. We deliberately do NOT translate the page wrapper because a
  // CSS transform on an ancestor would break our `position: fixed` bottom nav.
  useSwipeBack();

  return (
    <CallProvider user={user}>
      <RealtimeProvider user={user}>
        <LogoLayoutProvider>
        <LayoutGroup id="allure-route-layout">
          <div style={{ position: 'relative', minHeight: '100dvh' }}>
            <AnimatePresence initial={false} mode="sync">
              <motion.div
                key={location.pathname}
                initial={false}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  minHeight: '100dvh',
                  // Belt-and-suspenders: explicit opacity: 1 on the style so
                  // that even if framer-motion never fires its animation
                  // (Android WebView renderer restart, prefers-reduced-motion,
                  // JS thread stall) the page can NEVER be stuck invisible.
                  // Without this, the AdminReturnPill was the only thing
                  // visible on some Capacitor-Android launches because the
                  // whole route wrapper was frozen at opacity 0.
                  opacity: 1,
                }}
              >
                <AppRoutes
                  handleLogin={handleLogin}
                  handleLogout={handleLogout}
                  handleRoleSelect={handleRoleSelect}
                  routeLocation={location}
                  showSplash={showSplash}
                  handleSplashComplete={handleSplashComplete}
                  user={user}
                  loading={loading}
                />
              </motion.div>
            </AnimatePresence>
          </div>
        </LayoutGroup>
        <FloatingLogo hidden={showSplash} />
        <EdgeSwipeHint pathname={location.pathname} />
        <AdminReturnPill user={user} />
        <BlankScreenRecovery />
        <InAppBrowserBanner />
        <CookieConsentBanner />
        <PasskeyEnrollmentPrompt user={user} />
        <EnableNotificationsPrompt user={user} />
        <AgreementReAcceptGate user={user} />
      </LogoLayoutProvider>
      </RealtimeProvider>
    </CallProvider>
  );
}

function ThemedToaster() {
  const { resolved } = useTheme();
  return (
    <Toaster
      position="top-center"
      theme={resolved}
      toastOptions={{
        style: {
          background: "var(--surface)",
          color: "var(--text)",
          border: "1px solid var(--hairline)",
          borderRadius: "14px",
          fontSize: "13px",
          fontFamily: "Manrope, sans-serif",
        },
      }}
    />
  );
}

function App() {
  // Custom Google OAuth (Allurexp-branded consent screen). When the flag is
  // off we render the app without the provider so we cleanly fall back to the
  // legacy Emergent-managed flow.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const useCustomGoogleOAuth = String(process.env.REACT_APP_USE_CUSTOM_GOOGLE_OAUTH || '').toLowerCase() === 'true';
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

  const inner = (
    <BrowserRouter>
      <ThemedToaster />
      <ImpersonationBanner />
      <AnnouncementBanner />
      <IOSInstallBanner />
      <AppContent />
    </BrowserRouter>
  );

  const withTheme = <ThemeProvider>{inner}</ThemeProvider>;
  const withLanguage = <LanguageProvider>{withTheme}</LanguageProvider>;

  if (useCustomGoogleOAuth && googleClientId) {
    return <GoogleOAuthProvider clientId={googleClientId}>{withLanguage}</GoogleOAuthProvider>;
  }
  return withLanguage;
}

export default App;
;
